// `render(message, ctx)` — a `Message { key, params }` from the wire becomes one sentence (ARCH §2.7, API §1.7).
//
// Param policy (REQ-261 AC5 — the catalogue never formats a digit):
//   - `text` params are inserted verbatim: the sender pre-formatted them (a metric label, a rendered reason,
//     an amount in the metric's own context).
//   - `money` / `number` / `pct` / `date` / `instant` params are formatted by `@sl/core/format` in the context the
//     template pins (`{amount:money.exact}`) or, unpinned, by the ARCH §8.5 default: money by value
//     (`money.sentence`), a number as a grouped `count`, a percentage as a whole `pct.share`, a date as
//     `date.day`, an instant as `time.clock` in the device zone.
//   - `count` params render as "3 ad sets" — the digits from the formatter's `count` context, the noun from
//     the catalogue by the locale's plural rule (`{n:noun}` gives the noun alone, `{n:number}` the digits).
//   - `window`, `entity` (`name`, or `{e:level}` for its level word) and `resultType` (`plurals` default,
//     `plural`, `singular`) become words from the catalogue.
//   - `message` params (0.3.13, T-383) are NESTED messages: rendered in place by this same function, so a
//     phrase the catalogue can build is never pre-rendered by a server (§9.3's `scope`). The inner parts are
//     spliced, so a figure inside a phrase is still a `figure` part. Nesting is capped at `MAX_NESTING`.
//   - `metric` params (0.3.13, T-383) are a metric KEY: the word is `metricDefinition(v).label` from the
//     metrics table. They also decide the FORM of a figure in the same message — an UNPINNED `money` /
//     `number` / `pct` slot there takes its context from `metricLimitContext` instead of the by-type
//     default (a slot named `limit` as the owner's own amount, any other as a measured figure), which is
//     how a typed threshold reads "$25" on a money metric and "$2.00 back per $1" on return on ad spend
//     without the sender choosing either (API §1.7). `{limit:metric.limit}` / `{value:metric.value}` say
//     the same thing explicitly; the catalogue leaves the slots unpinned so its English stays byte-for-byte
//     what the C# mirror compares against (D-964).
// Policy on a bad message: `ctx.strict` (tests, development) throws a `MessageError`; production renders what it
// can — the key for an unknown key, "—" for a missing or mistyped param — so a contract slip never blanks a
// screen. Apps pass `strict: import.meta.env.DEV` / `__DEV__`; without it, Node's NODE_ENV decides.

import {
  FORMAT_CONTEXTS,
  NO_VALUE,
  createFormatter,
  type FormatContext,
  type FormatEnv,
  type FormatValueByContext,
} from '../format/format';
import {
  metricDefinition,
  metricLimitContext,
  type MetricFigureRole,
  type MetricRegistryKey,
} from '../metrics/registry';
import type { Message, MessageParam } from '../types/primitives';
import { en, type Catalogue, type PluralForms } from './en';
import { detectDev } from './env';
import { isMessageKey, messageKeySpec, type MessageKey, type MessageParamType } from './keys';
import { isResultTypeKey } from './resultNouns';

/** A formatter bound to one environment — what `createFormatter(env)` returns. */
export type BoundFormatter = <C extends FormatContext>(
  value: FormatValueByContext[C],
  context: C,
) => string;

export interface MessageContext {
  /** Formats every digit (REQ-261 AC5). Bound to the account's currency and zone and the user's locale. */
  format: BoundFormatter;
  /** BCP 47 tag — decides the plural category of a `count` (`Intl.PluralRules`). */
  locale: string;
  /** IANA zone of the device (REQ-261 AC4) — an `instant` param needs it; `time.ts` uses it for "Yesterday". */
  deviceTimeZone?: string;
  /** Throw on an unknown key / missing param (development, tests) instead of degrading (production). */
  strict?: boolean;
  /** The words. Defaults to English. */
  catalogue?: Catalogue;
}

export interface MessageContextOptions {
  strict?: boolean;
  catalogue?: Catalogue;
}

/** `createMessageContext({ account, locale, deviceTimeZone }, { strict: import.meta.env.DEV })`. */
export function createMessageContext(
  env: FormatEnv,
  options: MessageContextOptions = {},
): MessageContext {
  const ctx: MessageContext = {
    format: createFormatter(env),
    locale: env.locale,
    strict: options.strict ?? detectDev(),
  };
  if (env.deviceTimeZone !== undefined) ctx.deviceTimeZone = env.deviceTimeZone;
  if (options.catalogue) ctx.catalogue = options.catalogue;
  return ctx;
}

export type MessageErrorCode =
  | 'unknown_key'
  | 'missing_param'
  | 'wrong_param_type'
  | 'unknown_unit'
  | 'bad_modifier'
  | 'format_failed'
  /** 0.3.13: a `message` param nested deeper than `MAX_NESTING`, or one that contains itself. */
  | 'nested_too_deep';

/** Thrown in strict mode for a message the catalogue cannot render faithfully. */
export class MessageError extends Error {
  readonly code: MessageErrorCode;
  readonly key: string;
  constructor(code: MessageErrorCode, key: string, detail: string) {
    super(`messages: ${code} — ${detail} (key "${key}")`);
    this.name = 'MessageError';
    this.code = code;
    this.key = key;
  }
}

/** Renders a message to one string. */
export function render(message: Message, ctx: MessageContext): string {
  return renderParts(message, ctx)
    .map((part) => part.text)
    .join('');
}

/**
 * One piece of a rendered sentence (T-290, D-439 / D-504).
 *
 *   • `figure` — exactly what the FORMATTER produced: a `money` / `number` / `pct` / `date` / `instant` param, or
 *     the digits of a `count` param (its noun is a separate `text` part). A screen sets these in bold.
 *   • `entity` — an `{t:'entity'}` param rendered by name (`{entity}` / `{entity:name}`); carries the entity's
 *     `id` and `level` so a screen can link it without searching the sentence for its name. `{entity:level}`
 *     (the level word) is `text`.
 *   • `text` — everything else: the template's own words, `text` params (pre-formatted by the sender), the
 *     window / result-type / level words, a count's noun, and `NO_VALUE` where production degraded a bad param.
 *     Adjacent text is one part.
 *
 * `param` is the slot's name on a figure or entity part, and on a text part that came whole from one slot.
 */
export type MessagePart =
  | { kind: 'text'; text: string; param?: string }
  | { kind: 'figure'; text: string; param: string }
  | { kind: 'entity'; text: string; param: string; id: string; level: MessageEntityLevel };

type MessageEntityLevel = Extract<MessageParam, { t: 'entity' }>['level'];

/**
 * How deep a `message` param may nest (0.3.13). One level is all §9.3's `scope` needs; the cap exists so a
 * cyclic or absurd message from a server is a named failure rather than a stack overflow on a screen.
 */
export const MAX_NESTING = 4;

/**
 * `renderParts(message, ctx)` — the same sentence `render` gives, as typed parts, so a screen that bolds the
 * figures and links the entity (w02's briefing items, w11's first-run cards) never renders twice with a marking
 * formatter and never pattern-matches the finished string (D-439 / D-504 — both builds had written that trick out).
 * `render(m, ctx) === renderParts(m, ctx).map(p => p.text).join('')` always; adjacent literals are one part.
 * Policy on a bad message is `render`'s: strict throws, production degrades to the key / `NO_VALUE` as text.
 * A `message` param's own parts are spliced in, each keeping the INNER slot's name (0.3.13).
 */
export function renderParts(message: Message, ctx: MessageContext): MessagePart[] {
  return renderPartsAt(message, ctx, 0);
}

function renderPartsAt(message: Message, ctx: MessageContext, depth: number): MessagePart[] {
  const catalogue = ctx.catalogue ?? en;
  const key = message.key;
  if (depth > MAX_NESTING) {
    if (ctx.strict)
      throw new MessageError(
        'nested_too_deep',
        key,
        `a message param nested more than ${MAX_NESTING} deep`,
      );
    return [{ kind: 'text', text: NO_VALUE }];
  }
  const spec = messageKeySpec(key);
  const template = isMessageKey(key) ? catalogue.templates[key] : undefined;
  if (!spec || template === undefined) {
    if (ctx.strict)
      throw new MessageError('unknown_key', key, 'not in message-keys.json or the catalogue');
    return [{ kind: 'text', text: key }];
  }
  const params = message.params ?? {};
  const parts: MessagePart[] = [];
  const text = (t: string, param?: string) => {
    if (t === '') return;
    const last = parts[parts.length - 1];
    // Adjacent text is ONE part, as the marker trick's unmarked chunks were; `param` survives the merge only when
    // the whole piece came from one slot.
    if (last && last.kind === 'text') {
      last.text += t;
      if (last.param !== param) delete last.param;
      return;
    }
    parts.push(param === undefined ? { kind: 'text', text: t } : { kind: 'text', text: t, param });
  };
  for (const piece of parseTemplate(template)) {
    if (typeof piece === 'string') {
      text(piece);
      continue;
    }
    const expected = spec.params[piece.name];
    if (expected === undefined) {
      // A slot the table does not declare — a catalogue bug the tests catch; never a sender's fault.
      if (ctx.strict)
        throw new MessageError('bad_modifier', key, `slot {${piece.name}} is not a declared param`);
      text(NO_VALUE, piece.name);
      continue;
    }
    const param = params[piece.name];
    if (param === undefined) {
      if (ctx.strict)
        throw new MessageError('missing_param', key, `param "${piece.name}" is missing`);
      text(NO_VALUE, piece.name);
      continue;
    }
    if (!expected.includes(param.t)) {
      if (ctx.strict)
        throw new MessageError(
          'wrong_param_type',
          key,
          `param "${piece.name}" is "${param.t}", the table says ${expected.map((t) => `"${t}"`).join(' | ')}`,
        );
      text(NO_VALUE, piece.name);
      continue;
    }
    // 0.3.13: a numeric slot in a message that carries a `metric` param takes the context THAT METRIC
    // prescribes — resolved here, so `renderParamParts` only ever sees a real context and the figure's form
    // is never a sender's choice (API §1.7).
    const modifier = metricContext(key, piece, param, params, ctx);
    if (modifier === FAILED) {
      text(NO_VALUE, piece.name);
      continue;
    }
    for (const part of renderParamParts(key, piece.name, param, modifier, ctx, catalogue, depth)) {
      if (part.kind === 'text') text(part.text, part.param);
      else parts.push(part);
    }
  }
  return parts;
}

/** `metricContext`'s "this slot cannot be rendered" answer — distinct from `undefined`, which means "no modifier". */
const FAILED = Symbol('failed');

/** The explicit form of the same idea: a slot may spell out `{limit:metric.limit}` / `{value:metric.value}`. */
const METRIC_MODIFIER = /^metric\.(limit|value)$/;
/** The numeric param types whose context a `metric` param can prescribe. */
const METRIC_FIGURE_TYPES: readonly MessageParamType[] = ['money', 'number', 'pct'];
/** A slot named `limit` is a figure the OWNER set; any other is one the product MEASURED (§8.5 row 4 vs row 2). */
const roleOfSlot = (name: string): MetricFigureRole => (name === 'limit' ? 'limit' : 'value');

/**
 * The format context of one slot, with the `metric` rule applied (0.3.13, T-383).
 *
 * **The rule.** In a message that carries a `metric` param, a `money` / `number` / `pct` slot with **no
 * pinned context** reads in the context that metric prescribes (`metricLimitContext`) — `money.perDay` for a
 * money threshold, `ratio.roas` for return on ad spend, `pct.rate` for a click rate, and so on. A slot may
 * also spell it out as `{limit:metric.limit}` / `{value:metric.value}`; both forms resolve identically.
 *
 * **Why the default and not the explicit pin in the catalogue.** `AlertWords` on the C# side is held to
 * `en.ts` by a `Category=Contract` fact that compares the templates character for character (D-964), so
 * pinning a new hint in a template would turn `backend-test` red in whichever lane happens to be in
 * `backend/**` that hour. The shapes on the wire moved in 0.3.13; the catalogue's own English did not, and
 * that is worth keeping: the mirror is the thing holding the two catalogues together.
 *
 * Anything else is returned unchanged, so every other key keeps §8.5's by-type default (`DEFAULT_CONTEXT`).
 * `FAILED` means the slot cannot be rendered — a `metric` the registry does not know, or an explicit
 * `metric.*` pin with no `metric` param beside it; strict throws, production degrades to `NO_VALUE`.
 */
function metricContext(
  key: string,
  piece: TemplateSlot,
  param: MessageParam,
  params: Readonly<Record<string, MessageParam>>,
  ctx: MessageContext,
): string | undefined | typeof FAILED {
  const explicit = piece.modifier === undefined ? null : METRIC_MODIFIER.exec(piece.modifier);
  const implicit = piece.modifier === undefined && METRIC_FIGURE_TYPES.includes(param.t);
  if (explicit === null && !implicit) return piece.modifier;
  const metric = Object.values(params).find((p) => p.t === 'metric');
  if (metric === undefined) {
    // Unpinned and no metric: every other numeric slot in the catalogue, which keeps the by-type default.
    if (explicit === null) return undefined;
    if (ctx.strict)
      throw new MessageError(
        'bad_modifier',
        key,
        `{${piece.name}:${piece.modifier}} needs a "metric" param in the same message`,
      );
    return FAILED;
  }
  const role = explicit === null ? roleOfSlot(piece.name) : (explicit[1] as MetricFigureRole);
  try {
    return metricLimitContext(metric.v as MetricRegistryKey, role);
  } catch (error) {
    if (ctx.strict)
      throw new MessageError(
        'bad_modifier',
        key,
        `{${piece.name}} takes its context from the message's metric: ${error instanceof Error ? error.message : String(error)}`,
      );
    return FAILED;
  }
}

/** `renderKey('status.scheduled.long', { date: { t: 'date', v: '2026-10-01' } }, ctx)` — a `render` shorthand. */
export function renderKey(
  key: MessageKey,
  params: Record<string, MessageParam>,
  ctx: MessageContext,
): string {
  return render({ key, params }, ctx);
}

// ---- templates --------------------------------------------------------------------------------------------

export interface TemplateSlot {
  name: string;
  modifier?: string;
}

const SLOT = /\{([a-z][A-Za-z0-9]*)(?::([a-z][A-Za-z0-9.]*))?\}/g;
const parsed = new Map<string, readonly (string | TemplateSlot)[]>();

/** The literal pieces and `{slots}` of a template — memoised; exported for the catalogue tests. */
export function parseTemplate(template: string): readonly (string | TemplateSlot)[] {
  const hit = parsed.get(template);
  if (hit) return hit;
  const pieces: (string | TemplateSlot)[] = [];
  let last = 0;
  for (const match of template.matchAll(SLOT)) {
    const index = match.index ?? 0;
    if (index > last) pieces.push(template.slice(last, index));
    const slot: TemplateSlot = { name: match[1] as string };
    if (match[2] !== undefined) slot.modifier = match[2];
    pieces.push(slot);
    last = index + match[0].length;
  }
  if (last < template.length) pieces.push(template.slice(last));
  parsed.set(template, pieces);
  return pieces;
}

/** The slots of a template, in order. */
export function templateSlots(template: string): TemplateSlot[] {
  return parseTemplate(template).filter((p): p is TemplateSlot => typeof p !== 'string');
}

// ---- modifiers -----------------------------------------------------------------------------------------------

const MONEY_CONTEXTS: readonly FormatContext[] = [
  'money.exact',
  'money.cost',
  'money.perDay',
  'money.headline',
  'money.sentence',
  'money.compact',
  'ratio.roas',
];
const NUMBER_CONTEXTS: readonly FormatContext[] = ['count', 'count.compact', 'ratio.frequency'];
const PCT_CONTEXTS: readonly FormatContext[] = ['pct.rate', 'pct.share', 'pct.change'];
const DATE_CONTEXTS: readonly FormatContext[] = ['date.day', 'date.short'];
const INSTANT_CONTEXTS: readonly FormatContext[] = ['time.clock'];

const DEFAULT_CONTEXT: Partial<Record<MessageParamType, FormatContext>> = {
  money: 'money.sentence',
  number: 'count',
  pct: 'pct.share',
  date: 'date.day',
  instant: 'time.clock',
};

/**
 * 0.3.13: the two modifiers that name no context of their own but BORROW one from the message's `metric`
 * param — `metric.limit` for a user-set threshold, `metric.value` for an observed figure. Accepted on the
 * three numeric types, because which of them a limit arrives as is the metric's business (API §1.7).
 */
const METRIC_CONTEXTS: readonly string[] = ['metric.limit', 'metric.value'];

/** The modifiers a param type accepts (`[]` = none). Exported for the catalogue tests. */
export function allowedModifiers(type: MessageParamType): readonly string[] {
  switch (type) {
    case 'money':
      return [...MONEY_CONTEXTS, ...METRIC_CONTEXTS];
    case 'number':
      return [...NUMBER_CONTEXTS, ...METRIC_CONTEXTS];
    case 'pct':
      return [...PCT_CONTEXTS, ...METRIC_CONTEXTS];
    case 'date':
      return DATE_CONTEXTS;
    case 'instant':
      return INSTANT_CONTEXTS;
    case 'count':
      return ['noun', 'number'];
    case 'entity':
      return ['name', 'level'];
    case 'resultType':
      return ['plurals', 'plural', 'singular'];
    case 'window':
    case 'message':
    case 'metric':
    case 'text':
      return [];
  }
}

function checkModifier(
  key: string,
  name: string,
  type: MessageParamType,
  modifier: string | undefined,
  ctx: MessageContext,
): string | undefined {
  if (modifier === undefined) return undefined;
  if (allowedModifiers(type).includes(modifier)) return modifier;
  if (ctx.strict)
    throw new MessageError(
      'bad_modifier',
      key,
      `{${name}:${modifier}} is not a modifier for a ${type} param`,
    );
  return undefined;
}

/** One param's pieces — what `renderParts` splices in and `render` joins (the figure / noun split of a `count`). */
function renderParamParts(
  key: string,
  name: string,
  param: MessageParam,
  rawModifier: string | undefined,
  ctx: MessageContext,
  catalogue: Catalogue,
  depth: number,
): MessagePart[] {
  const modifier = checkModifier(key, name, param.t, rawModifier, ctx);
  const text = (t: string): MessagePart => ({ kind: 'text', text: t, param: name });
  const figure = (t: string): MessagePart => ({ kind: 'figure', text: t, param: name });
  switch (param.t) {
    case 'text':
      return [text(param.v)];
    case 'message':
      // The nested message's own parts, so a figure inside a phrase stays a figure and keeps the INNER
      // slot's name — the specific one, which is what a screen that marks a part would want (0.3.13).
      return renderPartsAt(param.v, ctx, depth + 1);
    case 'metric':
      return [text(metricLabel(key, name, param.v, ctx))];
    case 'money':
    case 'number':
    case 'pct':
    case 'date':
    case 'instant': {
      const context = (modifier ?? DEFAULT_CONTEXT[param.t]) as FormatContext;
      return [figure(formatValue(key, name, param.v, context, ctx))];
    }
    case 'count': {
      const digits = formatValue(key, name, param.v, 'count', ctx);
      if (modifier === 'number') return [figure(digits)];
      const noun = countNoun(key, name, param.unit, param.v, ctx, catalogue);
      if (modifier === 'noun') return [text(noun)];
      return noun === '' ? [figure(digits)] : [figure(digits), text(` ${noun}`)];
    }
    case 'window':
      return [text(catalogue.templates[`window.${param.v}`])];
    case 'entity':
      return modifier === 'level'
        ? [text(catalogue.templates[`level.${param.level}`])]
        : [{ kind: 'entity', text: param.name, param: name, id: param.id, level: param.level }];
    case 'resultType': {
      const noun = catalogue.resultNouns[param.v];
      if (modifier === 'plural') return [text(noun.plural)];
      if (modifier === 'singular') return [text(noun.singular)];
      return [text(noun.plurals)];
    }
  }
}

/**
 * A `metric` param's word: the metrics table's own `label` (REQ §7 — binding for UI copy). An unknown key is
 * a contract slip, not a missing label: strict throws, production degrades, and in neither case does the
 * renderer invent a word for a metric the product does not name.
 */
function metricLabel(key: string, name: string, metric: string, ctx: MessageContext): string {
  try {
    return metricDefinition(metric as MetricRegistryKey).label;
  } catch (error) {
    if (ctx.strict)
      throw new MessageError(
        'wrong_param_type',
        key,
        `param "${name}": ${error instanceof Error ? error.message : String(error)}`,
      );
    return NO_VALUE;
  }
}

function formatValue(
  key: string,
  name: string,
  value: FormatValueByContext[FormatContext],
  context: FormatContext,
  ctx: MessageContext,
): string {
  if (!FORMAT_CONTEXTS.includes(context)) {
    if (ctx.strict)
      throw new MessageError('bad_modifier', key, `{${name}:${context}} is not a format context`);
    return NO_VALUE;
  }
  try {
    return ctx.format(value, context);
  } catch (error) {
    if (ctx.strict)
      throw new MessageError(
        'format_failed',
        key,
        `param "${name}": ${error instanceof Error ? error.message : String(error)}`,
      );
    return NO_VALUE;
  }
}

const pluralRules = new Map<string, Intl.PluralRules>();

function pluralCategory(locale: string, n: number): Intl.LDMLPluralRule {
  let rules = pluralRules.get(locale);
  if (!rules) {
    rules = new Intl.PluralRules(locale);
    pluralRules.set(locale, rules);
  }
  return rules.select(n);
}

function pickForm(forms: PluralForms, category: Intl.LDMLPluralRule): string {
  return forms[category] ?? forms.other;
}

function countNoun(
  key: string,
  name: string,
  unit: string,
  n: number,
  ctx: MessageContext,
  catalogue: Catalogue,
): string {
  const category = pluralCategory(ctx.locale, n);
  const forms = catalogue.units[unit];
  if (forms) return pickForm(forms, category);
  if (isResultTypeKey(unit)) {
    const noun = catalogue.resultNouns[unit];
    return pickForm({ one: noun.singular, other: noun.plurals }, category);
  }
  if (ctx.strict)
    throw new MessageError(
      'unknown_unit',
      key,
      `count "${name}" has unit "${unit}" — not a unit word or a result type`,
    );
  return '';
}

// ---- environment ---------------------------------------------------------------------------------------------

// `detectDev` lives in `./env` since T-322 (so `keys.ts` can read it without this module); re-exported unchanged.
export { detectDev } from './env';
